import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  ResourceUIProvider,
  type CrudOp,
  type IntrospectableSchema,
  type ResourceMutations,
} from '@diadraw/ui';
import { schema } from '../__generated__/schema-lite';
import { can, canField } from '../lib/policy';
import { useResourceMutation } from '../hooks/useResourceMutation';
import { translateError } from '../lib/translate-error';
import { toTitleCase } from '../lib/to-title-case';

// schema-lite is structurally an IntrospectableSchema (models + enums).
const lite = schema as unknown as IntrospectableSchema;
// Adapt our typed policy `can`/`canField` to the kit's loose signatures.
const canLoose = can as unknown as (
  model: string,
  op: CrudOp,
  auth: unknown,
  row?: unknown,
) => boolean;
const canFieldLoose = canField as unknown as (
  model: string,
  field: string,
  op: CrudOp,
  auth: unknown,
  row?: unknown,
) => boolean;
const useMutationLoose = useResourceMutation as unknown as (model: string) => ResourceMutations;

/**
 * Wires the @diadraw/ui kit to THIS project once: our generated schema, i18n
 * labels/texts, `.input` Tailwind styling and the `can` policy evaluator. The
 * only place project specifics meet the kit — components elsewhere just render.
 */
export function AppUIProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <ResourceUIProvider
      schema={lite}
      label={(model, field) => t(`fields.${model}.${field}`, toTitleCase(field))}
      enumLabel={(_model, _field, value) => t(`enums.${value}`, value)}
      can={canLoose}
      canField={canFieldLoose}
      useMutation={useMutationLoose}
      formatError={(err) => translateError(t, err)}
      notify={(msg) => toast.success(msg)}
      theme={{
        input: 'input',
        // Lobby table look — headers as mono-ish uppercase muted micro-labels on
        // no fill, hairline row dividers, white paper body. Overrides the kit's
        // default grey-filled header.
        table: {
          // The table lives inside a padded white paper card (soft shadow).
          container:
            'overflow-x-auto rounded-2xl border border-border bg-card p-2 sm:p-3 shadow-[0_1px_2px_rgba(21,23,28,0.05),0_6px_20px_rgba(21,23,28,0.04)]',
          table: 'w-full text-sm bg-card',
          thead: 'text-left',
          th: 'px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground whitespace-nowrap border-b border-border',
          td: 'px-3 py-3 align-middle',
          row: 'border-t border-border',
          rowSelected: 'bg-accent',
          pageBtn: 'rounded-lg border border-border px-3 py-1 hover:bg-muted disabled:opacity-40',
        },
        filters: {
          root: 'flex flex-wrap items-end gap-3 rounded-2xl border border-border bg-card p-3 sm:p-4 shadow-[0_1px_2px_rgba(21,23,28,0.05),0_6px_20px_rgba(21,23,28,0.04)]',
          field: 'flex flex-col gap-1',
          label: 'text-xs font-medium',
          clear:
            'inline-flex items-center justify-center rounded-lg border border-border px-3 py-2 text-base leading-none text-muted-foreground hover:text-foreground hover:bg-muted',
        },
        form: {
          root: 'rounded-md border border-border bg-muted/50 p-3 sm:p-4 space-y-3',
          label: 'text-xs font-medium text-foreground',
          submit: 'btn-primary w-full sm:w-auto',
          cancel: 'rounded border border-border px-3 py-1.5 text-sm hover:bg-muted w-full sm:w-auto',
        },
      }}
      texts={{
        table: {
          error: t('common.error'),
          loading: t('common.loading'),
          empty: t('table.empty'),
          page: t('table.page'),
          prev: t('table.prev'),
          next: t('table.next'),
        },
        filters: {
          all: t('filters.all'),
          clear: '×',
          yes: t('filters.yes'),
          no: t('filters.no'),
          min: t('filters.min'),
          max: t('filters.max'),
          from: t('filters.from'),
          to: t('filters.to'),
        },
        form: {
          create: t('action.create'),
          save: t('action.save'),
          cancel: t('action.cancel'),
          saving: t('form.saving'),
          invalidJson: t('form.invalidJson'),
          required: t('form.required', 'Required'),
          createHeader: (m) => t('form.createHeader', { modelName: m }),
          editHeader: (m) => t('form.editHeader', { modelName: m }),
          created: (m) => t('form.created', { modelName: m }),
          updated: (m) => t('form.updated', { modelName: m }),
        },
      }}
    >
      {children}
    </ResourceUIProvider>
  );
}
